import { useEffect, useRef, useState } from 'react'

import * as d3 from 'd3'
import { FormattedMessage } from 'react-intl'

import Theme from '@theme'
import { Text } from '@tribegroup'
import {
  ChartContainer,
  ChartTitle,
  ResetButton,
  SVGContainer,
  Tooltip,
} from './PackedBubbleChart.styled'
import { IBubbleNode, IPackedBubbleChartProps } from './PackedBubbleChart.types'

const DEFAULT_COLORS = [
  '#FF6B6B',
  '#4ECDC4',
  '#FFE66D',
  '#95E1D3',
  '#F38181',
  '#AA96DA',
  '#FCBAD3',
  '#A8D8EA',
  '#FFD93D',
  '#6BCB77',
]

// Calculate luminance to determine if text should be white or black
const getContrastTextColor = (backgroundColor: string): string => {
  // Remove # if present
  const hex = backgroundColor.replace('#', '')

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255
  const g = parseInt(hex.substring(2, 4), 16) / 255
  const b = parseInt(hex.substring(4, 6), 16) / 255

  // Calculate relative luminance (WCAG formula)
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b

  // Return white for dark backgrounds, black for light backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF'
}

const PackedBubbleChart: React.FC<IPackedBubbleChartProps> = ({
  title,
  data,
  width,
  height = 500,
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null)
  const initialTransformRef = useRef<d3.ZoomTransform | null>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [isZoomed, setIsZoomed] = useState(false)

  // Track if we should show empty state
  const showEmptyState = !data || data.length === 0

  // Handle responsive sizing
  useEffect(() => {
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width: containerWidth } = entries[0].contentRect
        setDimensions({
          width: width || containerWidth,
          height,
        })
      }
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [width, height])

  // D3 force simulation and rendering (handles both data and empty states)
  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove() // Clear previous render

    if (showEmptyState) {
      // Reset zoom state when switching to empty (prevents stale Reset button)
      setIsZoomed(false)
      zoomRef.current = null
      initialTransformRef.current = null

      // Render empty state using D3
      svg.attr('width', '100%').attr('height', 150).attr('viewBox', '0 0 400 150')

      // Skeleton bubbles
      const skeletonData = [
        { cx: 200, cy: 60, r: 35 },
        { cx: 120, cy: 85, r: 28 },
        { cx: 280, cy: 75, r: 30 },
        { cx: 170, cy: 115, r: 22 },
        { cx: 250, cy: 120, r: 25 },
      ]

      svg
        .selectAll('circle')
        .data(skeletonData)
        .join('circle')
        .attr('cx', (d) => d.cx)
        .attr('cy', (d) => d.cy)
        .attr('r', (d) => d.r)
        .attr('fill', Theme.grey300Color)
        .attr('opacity', 0.6)

      return
    }

    // Render data state
    const { width: chartWidth, height: chartHeight } = dimensions

    svg
      .attr('width', chartWidth)
      .attr('height', chartHeight)
      .attr('viewBox', null)
      .style('cursor', 'grab')

    // Create scale for bubble size based on relative relevance
    const maxRelevance = d3.max(data, (d) => d.relativeRelevance) || 1
    const minRadius = 30
    const maxRadius = Math.max(minRadius, Math.min(chartWidth, chartHeight) / 6)
    const radiusScale = d3.scaleSqrt().domain([0, maxRelevance]).range([minRadius, maxRadius])

    // Prepare nodes with initial positions
    const nodes: IBubbleNode[] = data.map((d, i) => ({
      ...d,
      x: chartWidth / 2,
      y: chartHeight / 2,
      r: radiusScale(d.relativeRelevance),
      color: d.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
    }))

    // Find the largest bubble to anchor to center
    const largestBubble = nodes.reduce((max, node) => (node.r > max.r ? node : max), nodes[0])

    // Create force simulation with collision detection and largest-bubble centering
    const simulation = d3
      .forceSimulation(nodes)
      .force('charge', d3.forceManyBody().strength(10)) // Strong repulsion between bubbles
      .force(
        'collide',
        d3
          .forceCollide<IBubbleNode>()
          .radius((d) => d.r + 4) // 4px padding for clean separation
          .iterations(10), // High iteration count for precise collision resolution
      )
      .force('center', d3.forceCenter(chartWidth / 2, chartHeight / 2)) // Basic centering (no strength API)
      .force('x', d3.forceX(chartWidth / 2).strength(0.01)) // Weak X-axis centering pull
      .force('y', d3.forceY(chartHeight / 2).strength(0.01)) // Weak Y-axis centering pull (fixed: was chartWidth)
      .force('anchorLargest', () => {
        // Keep largest bubble near center via velocity adjustment (not position override)
        // This allows collision detection to still push it away if needed
        if (largestBubble) {
          const centerX = chartWidth / 2
          const centerY = chartHeight / 2
          const strength = 0.1 // Gentle centering strength

          largestBubble.vx = largestBubble.vx || 0
          largestBubble.vy = largestBubble.vy || 0

          largestBubble.vx += (centerX - (largestBubble.x || centerX)) * strength
          largestBubble.vy += (centerY - (largestBubble.y || centerY)) * strength
        }
      })

    // Run simulation until convergence with adaptive tick count to prevent main-thread jank
    // Small datasets need more ticks per node for collision resolution to stabilize
    const maxTicks = Math.min(600, Math.max(150, 50 * nodes.length))
    const alphaMin = 0.05 // Stop when simulation energy drops below this threshold

    let tickCount = 0
    while (simulation.alpha() > alphaMin && tickCount < maxTicks) {
      simulation.tick()
      tickCount += 1
    }
    simulation.stop()

    // Create a canvas group that will be transformed by zoom/pan
    const canvas = svg.append('g').attr('class', 'canvas')

    // Create bubble groups with tooltip event handlers
    const bubbleGroups = canvas
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('transform', (d) => `translate(${d.x},${d.y})`)
      .attr('data-testid', 'bubble-group')
      .attr('opacity', 0) // Start invisible
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        // Highlight bubble
        d3.select(this).select('circle').attr('opacity', 0.8).attr('stroke-width', 3)

        // Show tooltip
        const tooltip = d3.select(tooltipRef.current)
        tooltip
          .style('opacity', 1)
          .style('visibility', 'visible')
          .html(
            `<div style="font-weight: 600; margin-bottom: 4px;">${d.label}</div>` +
              `<div style="font-size: 12px;">Relative Relevance: <strong>${d.relativeRelevance.toFixed(
                2,
              )}x</strong></div>`,
          )
      })
      .on('mousemove', function (event) {
        // Position tooltip near cursor
        const tooltip = d3.select(tooltipRef.current)
        const containerRect = containerRef.current?.getBoundingClientRect()
        if (containerRect) {
          const x = event.clientX - containerRect.left + 10
          const y = event.clientY - containerRect.top - 10
          tooltip.style('left', `${x}px`).style('top', `${y}px`)
        }
      })
      .on('mouseout', function () {
        // Remove highlight
        d3.select(this).select('circle').attr('opacity', 1).attr('stroke-width', 2)

        // Hide tooltip
        const tooltip = d3.select(tooltipRef.current)
        tooltip.style('opacity', 0).style('visibility', 'hidden')
      })

    // Staggered fade-in animation (in data order)
    bubbleGroups
      .transition()
      .duration(400)
      .delay((d, i) => i * 100) // 100ms delay between each bubble (data order, not sorted by size)
      .ease(d3.easeQuadOut)
      .attr('opacity', 1)

    // Add circles
    bubbleGroups
      .append('circle')
      .attr('r', (d) => d.r)
      .attr('fill', (d) => d.color || Theme.primaryColor)
      .attr('opacity', 1)
      .attr('stroke', Theme.whiteColor)
      .attr('stroke-width', 2)

    // Add text labels
    bubbleGroups.each(function (d) {
      const group = d3.select(this)
      const textColor = getContrastTextColor(d.color || Theme.primaryColor)

      // Category name (wrapped text)
      const words = d.label.split(/\s+/)
      const lines: string[] = []
      let currentLine = ''

      // Simple word wrapping
      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word
        if (testLine.length * 6 < d.r * 1.8 || !currentLine) {
          currentLine = testLine
        } else {
          lines.push(currentLine)
          currentLine = word
        }
      })
      if (currentLine) {
        lines.push(currentLine)
      }

      // Limit to 2 lines with ellipsis
      const displayLines = lines.slice(0, 2)
      if (lines.length > 2) {
        displayLines[1] = displayLines[1] + '...'
      }

      // Position text elements
      const textYOffset = -displayLines.length * 6

      displayLines.forEach((line, i) => {
        group
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', textYOffset + i * 12)
          .attr('font-size', '11px')
          .attr('font-weight', '400')
          .attr('fill', textColor)
          .text(line)
      })

      // Relative relevance multiplier (with safety check)
      if (typeof d.relativeRelevance === 'number' && !isNaN(d.relativeRelevance)) {
        group
          .append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', Math.max(10, textYOffset + displayLines.length * 12 + 4))
          .attr('font-size', '14px')
          .attr('font-weight', '600')
          .attr('fill', textColor)
          .text(`${d.relativeRelevance.toFixed(2)}x`)
      }
    })

    // --- D3 Zoom/Pan (Canva-style draggable canvas) ---
    // Compute bounding box of all bubbles
    const padding = 20
    const minX = d3.min(nodes, (d) => (d.x || 0) - d.r - padding) || 0
    const maxX = d3.max(nodes, (d) => (d.x || 0) + d.r + padding) || chartWidth
    const minY = d3.min(nodes, (d) => (d.y || 0) - d.r - padding) || 0
    const maxY = d3.max(nodes, (d) => (d.y || 0) + d.r + padding) || chartHeight
    const contentWidth = maxX - minX
    const contentHeight = maxY - minY

    // Calculate initial transform to fit all bubbles in view
    const scaleX = chartWidth / contentWidth
    const scaleY = chartHeight / contentHeight
    const initialScale = Math.min(scaleX, scaleY, 1) // Never zoom in beyond 1x
    const tx = (chartWidth - contentWidth * initialScale) / 2 - minX * initialScale
    const ty = (chartHeight - contentHeight * initialScale) / 2 - minY * initialScale
    const fitTransform = d3.zoomIdentity.translate(tx, ty).scale(initialScale)

    initialTransformRef.current = fitTransform

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 2])
      .on('start', () => {
        svg.style('cursor', 'grabbing')
      })
      .on('zoom', (event) => {
        canvas.attr('transform', event.transform.toString())
        const isSameAsInitial =
          initialTransformRef.current &&
          Math.abs(event.transform.k - initialTransformRef.current.k) < 0.01 &&
          Math.abs(event.transform.x - initialTransformRef.current.x) < 1 &&
          Math.abs(event.transform.y - initialTransformRef.current.y) < 1
        setIsZoomed(!isSameAsInitial)
      })
      .on('end', () => {
        svg.style('cursor', 'grab')
      })

    svg.call(zoom)
    svg.call(zoom.transform, fitTransform)
    zoomRef.current = zoom

    return () => {
      if (svgRef.current) {
        d3.select(svgRef.current).on('.zoom', null)
        d3.select(svgRef.current).selectAll('*').remove()
      }
    }
  }, [data, dimensions, showEmptyState])

  const handleResetZoom = () => {
    if (!svgRef.current || !zoomRef.current || !initialTransformRef.current) return
    const svg = d3.select<SVGSVGElement, unknown>(svgRef.current)
    svg.transition().duration(300).call(zoomRef.current.transform, initialTransformRef.current)
    setIsZoomed(false)
  }

  return (
    <ChartContainer
      data-testid={showEmptyState ? 'packed-bubble-chart-empty' : 'packed-bubble-chart'}
    >
      {title && <ChartTitle>{title}</ChartTitle>}

      <SVGContainer ref={containerRef}>
        <svg ref={svgRef} />
        <Tooltip ref={tooltipRef} data-testid="bubble-tooltip" />
        {isZoomed && (
          <ResetButton
            onClick={handleResetZoom}
            data-testid="bubble-chart-reset-zoom"
            type="button"
          >
            <FormattedMessage id="audienceInsights.chart.reset_view" />
          </ResetButton>
        )}
      </SVGContainer>

      {showEmptyState && (
        <Text textAlign="center" color={Theme.grey700Color}>
          <FormattedMessage id="audienceInsights.chart.empty" />
        </Text>
      )}
    </ChartContainer>
  )
}

export default PackedBubbleChart
